import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  MdInfo,
  MdTune,
  MdError,
  MdDownload,
  MdRefresh,
  MdReplay,
  MdOutlineQueue,
} from "react-icons/md";
import OptimizedImage from "../OptimizedImage/OptimizedImage";
import ZoomButton from "../buttons/ZoomButton";
import { endpoint } from "../../api/endpoint";

const DOWNLOAD_FORMATS = [
  { label: "AVIF", value: "avif", extension: "avif" },
  { label: "PNG", value: "png", extension: "png" },
  { label: "JPEG", value: "jpeg", extension: "jpg" },
  { label: "PSD", value: "psd", extension: "psd" },
];

const parseCustomDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split(/[\s,/-:]+/);
  if (parts.length >= 3) {
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    if (year > 1000) {
      const hours = parts[3] ? parseInt(parts[3], 10) : 0;
      const minutes = parts[4] ? parseInt(parts[4], 10) : 0;
      const seconds = parts[5] ? parseInt(parts[5], 10) : 0;
      return new Date(year, month, day, hours, minutes, seconds);
    }
  }
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
};

const formatDisplayDate = (rawDate) => {
  const d = parseCustomDate(rawDate);
  if (!d) return rawDate;
  const day = d.getDate();
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = monthNames[d.getMonth()];
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  return `${day} ${month} - ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
};


const QueueItem = ({
  id,
  index = 0,
  product,
  model,
  background,
  generationParameters,
  result,
  date,
  status,
  generation_status,
  generationStatus,
  errorMessage,
  error_message,
  progress = 0,
  showLoader = false,
  showProgress = false,
  buttons = [],
  onZoom,
  onRegenerate,
  startTime,
  executionTime,
}) => {
  const downloadMenuRef = useRef(null);
  const parameterTooltipRef = useRef(null);
  const tooltipHideTimerRef = useRef(null);
  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
  const [parameterTooltipStyle, setParameterTooltipStyle] = useState(null);
  const normalizedStatus = String(status || "queued").toLowerCase();
  const isCurrentlyProcessing = normalizedStatus === "processing" || Boolean(showLoader);
  const [simulatedProgress, setSimulatedProgress] = useState(0);
  const [shouldShowLoader, setShouldShowLoader] = useState(isCurrentlyProcessing);
  const [imageFadeIn, setImageFadeIn] = useState(false);
  const initialOffsetRef = useRef(null);

  // Handle completion delay to smooth transition
  useEffect(() => {
    if (normalizedStatus === "processing" || showLoader) {
      setShouldShowLoader(true);
      setImageFadeIn(false);
    } else if (normalizedStatus === "completed") {
      const timer = setTimeout(() => {
        setShouldShowLoader(false);
        setTimeout(() => setImageFadeIn(true), 50);
      }, 800);
      return () => clearTimeout(timer);
    } else {
      setShouldShowLoader(false);
    }
  }, [showLoader, normalizedStatus]);

  useEffect(() => {
    const storageKey = `queue_offset_${id}`;

    if (normalizedStatus !== "processing" || !startTime) {
      if (normalizedStatus === "completed") {
        setSimulatedProgress(100);
      } else {
        setSimulatedProgress(0);
      }
      initialOffsetRef.current = null;
      if (id && (normalizedStatus === "completed" || normalizedStatus === "failed")) {
        sessionStorage.removeItem(storageKey);
      }
      return undefined;
    }

    const savedOffset = id ? sessionStorage.getItem(storageKey) : null;
    if (savedOffset !== null) {
      initialOffsetRef.current = Number(savedOffset);
    }

    const workflowValue = (generationParameters?.workflow || "").toLowerCase();
    const totalTime = workflowValue === "virtual_try_on" ? 40000 : 60000;
    const startTimestamp = new Date(startTime).getTime();

    const updateProgress = () => {
      const now = Date.now();
      const rawElapsed = now - startTimestamp;

      if (initialOffsetRef.current === null) {
        initialOffsetRef.current = rawElapsed;
        if (id) {
          sessionStorage.setItem(storageKey, String(rawElapsed));
        }
      }

      const elapsed = Math.max(0, rawElapsed - initialOffsetRef.current);
      let calculatedProgress = Math.floor((elapsed / totalTime) * 100);
      if (calculatedProgress >= 95) {
        const overtime = Math.max(0, elapsed - totalTime);
        const additionalProgress = Math.min(4, Math.floor(overtime / 10000)); // 1% more every 10 seconds, up to 99%
        calculatedProgress = Math.min(99, 95 + additionalProgress);
      }
      setSimulatedProgress(Math.max(1, calculatedProgress));
    };

    updateProgress();
    const interval = setInterval(updateProgress, 1000);
    return () => clearInterval(interval);
  }, [normalizedStatus, startTime, generationParameters?.workflow, progress, id]);

  const displayProgress =
    normalizedStatus === "processing" ? simulatedProgress : (normalizedStatus === "completed" ? 100 : progress || 0);

  const canDownloadResult =
    normalizedStatus === "completed" &&
    Boolean(result?.generatedImage || result?.image);
  const canRegenerate = Boolean(id) && normalizedStatus !== "processing";
  const showParameterTooltip = (event) => {
    // If the device is a touch screen, only allow displaying the tooltip via click events
    if (window.matchMedia("(pointer: coarse)").matches && event.type !== "click") {
      return;
    }

    window.clearTimeout(tooltipHideTimerRef.current);

    const triggerRect = event.currentTarget.getBoundingClientRect();
    const viewportPadding = 12;
    const gap = 8;
    const preferredWidth = 204;
    const width = Math.min(preferredWidth, window.innerWidth - viewportPadding * 2);
    const left = Math.min(
      Math.max(viewportPadding, triggerRect.right - width),
      window.innerWidth - width - viewportPadding,
    );
    const spaceBelow = window.innerHeight - triggerRect.bottom - viewportPadding;
    const spaceAbove = triggerRect.top - viewportPadding;
    const openAbove = spaceBelow < 190 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(100, Math.min(210, openAbove ? spaceAbove - gap : spaceBelow - gap));

    setParameterTooltipStyle({
      left,
      width,
      maxHeight,
      ...(openAbove
        ? { bottom: window.innerHeight - triggerRect.top + gap }
        : { top: triggerRect.bottom + gap }),
    });
  };

  const scheduleHideParameterTooltip = () => {
    if (window.matchMedia("(pointer: coarse)").matches) {
      return;
    }
    window.clearTimeout(tooltipHideTimerRef.current);
    tooltipHideTimerRef.current = window.setTimeout(() => {
      setParameterTooltipStyle(null);
    }, 240);
  };

  const keepParameterTooltipOpen = () => {
    window.clearTimeout(tooltipHideTimerRef.current);
  };

  useEffect(() => {
    return () => {
      window.clearTimeout(tooltipHideTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!parameterTooltipStyle) return undefined;

    const handleDocumentPointerDown = (event) => {
      const clickedTrigger = event.target.closest("[data-tooltip-trigger]");
      if (
        parameterTooltipRef.current &&
        !parameterTooltipRef.current.contains(event.target) &&
        !clickedTrigger
      ) {
        setParameterTooltipStyle(null);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        setParameterTooltipStyle(null);
      }
    };

    document.addEventListener("pointerdown", handleDocumentPointerDown);
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("pointerdown", handleDocumentPointerDown);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [parameterTooltipStyle]);

  const toggleParameterTooltip = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (parameterTooltipStyle) {
      setParameterTooltipStyle(null);
      return;
    }

    showParameterTooltip(event);
  };

  const generationParameterEntries = Object.entries(generationParameters || {});

  const filteredGenerationParameterEntries = generationParameterEntries.filter(
    ([key]) => {
      const k = String(key || "").toLowerCase();
      return k !== "workflow" && k !== "gender";
    },
  );

  const hasInfoIcon = filteredGenerationParameterEntries.length > 0;

  const formatGenerationValue = (value) => {
    if (value === "" || value === null || value === undefined) return "Default";
    if (typeof value === "string" && !value.trim()) return "Default";
    if (Array.isArray(value))
      return value.length ? value.join(", ") : "Default";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  useEffect(() => {
    if (!isDownloadMenuOpen) return undefined;

    const handleDocumentClick = (event) => {
      if (
        downloadMenuRef.current &&
        !downloadMenuRef.current.contains(event.target)
      ) {
        setIsDownloadMenuOpen(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        setIsDownloadMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isDownloadMenuOpen]);

  const openImageZoom = (
    front,
    back = "",
    showFlip = false,
    isBackground = false,
  ) => {
    if (typeof onZoom === "function" && front) {
      onZoom(front, back, showFlip, isBackground);
    }
  };

  const modelImageIsBack =
    (generationParameters &&
      String(generationParameters.view || "").toLowerCase() === "back") ||
    (generationParameters &&
      String(generationParameters.pose || "").toLowerCase() === "back");

  const modelFrontImage = model
    ? modelImageIsBack
      ? model.back || model.image
      : model.image
    : "";

  const modelBackImage = model ? model.back || model.image : "";

  const getZoomImageSource = (imageUrl) => {
    if (typeof imageUrl !== "string" || !imageUrl.trim()) return "";

    try {
      const parsedUrl = new URL(imageUrl);
      const searchParams = parsedUrl.searchParams;
      const queryKeys = Array.from(searchParams.keys());

      if (queryKeys.length === 0) return imageUrl;

      const signedKeys = [
        "X-Amz-Signature",
        "X-Amz-Credential",
        "X-Amz-Security-Token",
        "X-Amz-Algorithm",
        "Signature",
        "Policy",
        "token",
        "sig",
      ];

      const hasSignedParams = queryKeys.some((key) =>
        signedKeys.some(
          (signedKey) => signedKey.toLowerCase() === key.toLowerCase(),
        ),
      );

      if (hasSignedParams) return imageUrl;

      const transformKeys = [
        "w",
        "h",
        "width",
        "height",
        "fit",
        "crop",
        "q",
        "quality",
        "dpr",
        "fm",
        "format",
        "auto",
      ];

      let removedAny = false;
      transformKeys.forEach((key) => {
        if (searchParams.has(key)) {
          searchParams.delete(key);
          removedAny = true;
        }
      });

      return removedAny ? parsedUrl.toString() : imageUrl;
    } catch {
      return imageUrl;
    }
  };

  const triggerDownload = (downloadBlob, filename) => {
    const blobUrl = window.URL.createObjectURL(downloadBlob);
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  const handleDownload = async (format) => {
    const downloadUrl = result?.generatedImage || result?.image;
    if (!downloadUrl) return;

    setIsDownloadMenuOpen(false);

    try {
      const selectedFormat = format || DOWNLOAD_FORMATS[0];
      const backendDownloadUrl = `${endpoint.API_BASE_URL}${endpoint.IMAGE_DOWNLOAD}?format=${selectedFormat.value}&url=${encodeURIComponent(downloadUrl)}`;

      const response = await fetch(backendDownloadUrl, { mode: "cors" });
      if (!response.ok) throw new Error("Backend download failed");

      const outputBlob = await response.blob();
      triggerDownload(
        outputBlob,
        `generated-image-${Date.now()}.${selectedFormat.extension}`,
      );
    } catch (_error) {
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    }
  };

  const toggleDownloadMenu = () => {
    setIsDownloadMenuOpen((currentValue) => !currentValue);
  };

  const handleRegenerate = () => {
    if (typeof onRegenerate === "function" && id) {
      onRegenerate(id, result?.generatedImage || result?.image || "");
    }
  };

  const backgroundName = background?.name || "Background";

  const queueItem = {
    status: String(status || "").toLowerCase(),
    generation_status: String(generation_status || generationStatus || "").toLowerCase(),
    error_message: error_message || errorMessage,
  };

  const displayErrorMessage =
    queueItem.status === "failed" || queueItem.generation_status === "failed"
      ? "We couldn't generate the image right now. Please try again in a few minutes."
      : queueItem.error_message;

  const renderParameterTooltip = () => {
    if (filteredGenerationParameterEntries.length === 0) return null;

    return (
      <div className="relative mb-1">
        <button
          type="button"
          data-tooltip-trigger="true"
          className={`h-7 w-7 rounded-full flex items-center justify-center border transition-colors cursor-pointer shadow-sm ${
            parameterTooltipStyle
              ? "bg-slate-50 text-primary border-outline"
              : "bg-white text-secondary hover:bg-slate-50 hover:text-primary border-outline/70"
          }`}
          aria-label="Generation parameters"
          aria-expanded={Boolean(parameterTooltipStyle)}
          onPointerEnter={showParameterTooltip}
          onMouseEnter={showParameterTooltip}
          onMouseLeave={scheduleHideParameterTooltip}
          onFocus={showParameterTooltip}
          onBlur={scheduleHideParameterTooltip}
          onClick={toggleParameterTooltip}
        >
          <MdInfo size={18} />
        </button>

        {parameterTooltipStyle && createPortal(
          <div
            ref={parameterTooltipRef}
            className="fixed z-[9999] overflow-y-auto rounded-md border border-outline bg-surface-container-lowest text-left shadow-[0_12px_28px_rgba(15,23,42,0.14)] animate-fade-in"
            style={parameterTooltipStyle}
            onPointerEnter={keepParameterTooltipOpen}
            onPointerLeave={scheduleHideParameterTooltip}
            onMouseEnter={keepParameterTooltipOpen}
            onMouseLeave={scheduleHideParameterTooltip}
          >
            <div className="sticky top-0 flex items-center gap-1.5 border-b border-outline/70 bg-surface-container-lowest px-2 py-1">
              <MdTune className="text-[15px] text-primary" />
              <p className="m-0 text-[9px] font-bold uppercase tracking-wider text-secondary">Generation parameters</p>
            </div>
            <div className="grid gap-1 p-1.5 text-[10px] text-secondary">
              <div className="rounded bg-surface-container px-2 py-1">
                <div className="flex justify-between gap-3">
                  <span>Background</span>
                  <span className="font-semibold text-primary text-right truncate max-w-[84px]" title={backgroundName}>
                    {backgroundName}
                  </span>
                </div>
              </div>
              <div className="rounded bg-surface-container px-2 py-1">
                <div className="flex justify-between gap-3">
                  <span>Workflow</span>
                  <span className="font-semibold text-primary capitalize">
                    {generationParameters?.workflow?.replace(/_/g, " ") || "VTO"}
                  </span>
                </div>
              </div>
              {filteredGenerationParameterEntries.map(([key, val]) => (
                <div key={key} className="rounded bg-surface-container px-2 py-1">
                  <div className="flex justify-between gap-3">
                    <span className="capitalize">{key.replace(/_/g, " ")}</span>
                    <span className="font-semibold text-primary text-right truncate max-w-[84px]" title={formatGenerationValue(val)}>
                      {formatGenerationValue(val)}
                    </span>
                  </div>
                </div>
              ))}
              <div className="rounded bg-surface-container px-2 py-1">
                <div className="flex justify-between gap-3">
                  <span>Progress</span>
                  <span className="font-semibold text-primary">
                    {normalizedStatus === "processing" || shouldShowLoader ? `${displayProgress}%` : (normalizedStatus === "completed" ? "Complete" : normalizedStatus === "queued" ? "In Queue" : "Failed")}
                  </span>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  };

  return (
    <article
      className={`relative bg-surface-container-lowest border border-outline/60 rounded-lg p-3 min-h-[150px] max-[640px]:min-h-[106px] transition-all duration-200 queue-item-card queue-item-${normalizedStatus} queue-item-animate ${parameterTooltipStyle ? 'z-[9998]' : 'z-10 hover:z-20'}`}
      style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
    >

      <div className="flex min-w-0 flex-col xl:flex-row gap-4 xl:items-start xl:pr-[170px] pb-2.5">
        <div className="flex-1 min-w-0">

          {/* Main asset input strip */}
          <div className="flex min-w-0 flex-nowrap items-start gap-3 max-[640px]:gap-1 max-[640px]:justify-between max-[640px]:pr-0">

            {/* Product card */}
            {product && (
              <div className="w-[116px] flex-[0_0_116px] min-w-0 max-[900px]:w-[92px] max-[900px]:flex-[0_0_92px] max-[640px]:w-[60px] max-[640px]:flex-[0_0_60px]">
                <div className="h-[120px] max-[900px]:h-[96px] max-[640px]:h-[70px] relative rounded-lg bg-surface-container border border-outline/70 overflow-hidden shadow-sm flex items-center justify-center">
                  <OptimizedImage
                    src={product.image || product.front || ""}
                    className="w-full h-full object-contain mix-blend-multiply"
                    alt="product"
                  />
                  {Boolean(product.image || product.front) && (
                    <ZoomButton
                      modalTarget="#queueZoomModal"
                      onClick={() =>
                        openImageZoom(
                          product.image || product.front,
                          product.back,
                          Boolean(product.back),
                          false,
                        )
                      }
                      ariaLabel={`Zoom ${product.name || "product"}`}
                      positionClasses="absolute bottom-1.5 right-1.5"
                      size="md"
                    />
                  )}
                </div>
                <p
                  className="mt-1.5 text-[11px] max-[640px]:text-[9px] font-semibold text-primary text-center leading-tight select-text w-full break-words max-[640px]:line-clamp-1"
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                  title={product.name || "Product"}
                >
                  {product.name || "Product"}
                </p>
              </div>
            )}

            {/* Operator Plus */}
            {product && model && (
              <span className="flex h-[120px] max-[900px]:h-[96px] max-[640px]:h-[70px] items-center justify-center text-xl max-[640px]:text-[11px] font-bold text-primary px-1 max-[640px]:px-0 max-[640px]:w-2 shrink-0 select-none">
                +
              </span>
            )}

            {/* Model card */}
            {model && (
              <div className="w-[116px] flex-[0_0_116px] min-w-0 max-[900px]:w-[92px] max-[900px]:flex-[0_0_92px] max-[640px]:w-[60px] max-[640px]:flex-[0_0_60px]">
                <div className="h-[120px] max-[900px]:h-[96px] max-[640px]:h-[70px] relative rounded-lg bg-surface-container border border-outline/70 overflow-hidden shadow-sm flex items-center justify-center">
                  <OptimizedImage
                    src={modelFrontImage}
                    className="w-full h-full object-contain object-top"
                    alt="model"
                  />
                  <ZoomButton
                    modalTarget="#queueZoomModal"
                    onClick={() =>
                      openImageZoom(
                        modelFrontImage,
                        modelBackImage,
                        Boolean(modelBackImage && modelBackImage !== modelFrontImage),
                        false,
                      )
                    }
                    ariaLabel={`Zoom ${model.name || "model"}`}
                    positionClasses="absolute bottom-1.5 right-1.5"
                    size="md"
                  />
                </div>
                <p
                  className="mt-1.5 text-[11px] max-[640px]:text-[9px] font-semibold text-primary text-center leading-tight select-text w-full break-words max-[640px]:line-clamp-1"
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                  title={model.name}
                >
                  {model.name}
                </p>
              </div>
            )}

            {/* Operator Plus */}
            {background && (
              <span className="flex h-[120px] max-[900px]:h-[96px] max-[640px]:h-[70px] items-center justify-center text-xl max-[640px]:text-[11px] font-bold text-primary px-1 max-[640px]:px-0 max-[640px]:w-2 shrink-0 select-none">
                +
              </span>
            )}

            {/* Background card */}
            {background && (
              <div className="w-[160px] flex-[0_0_160px] min-w-0 max-[900px]:w-[128px] max-[900px]:flex-[0_0_128px] max-[640px]:w-[93px] max-[640px]:flex-[0_0_93px]">
                <div className="h-[120px] max-[900px]:h-[96px] max-[640px]:h-[70px] relative rounded-lg bg-surface-container border border-outline/70 overflow-hidden shadow-sm flex items-center justify-center">
                  <OptimizedImage
                    src={background.image}
                    className="w-full h-full object-cover"
                    alt="background"
                  />
                  <ZoomButton
                    modalTarget="#queueZoomModal"
                    onClick={() => openImageZoom(background.image, "", false, true)}
                    ariaLabel={`Zoom ${background.name || "background"}`}
                    positionClasses="absolute bottom-1.5 right-1.5"
                    size="md"
                  />
                </div>
                <p
                  className="mt-1.5 text-[11px] max-[640px]:text-[9px] font-semibold text-primary text-center leading-tight select-text w-full break-words max-[640px]:line-clamp-1"
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                  title={backgroundName}
                >
                  {backgroundName}
                </p>
              </div>
            )}

            {/* Operator Equal */}
            <span className="flex h-[120px] max-[900px]:h-[96px] max-[640px]:h-[70px] items-center justify-center text-xl max-[640px]:text-[11px] font-bold text-primary px-1 max-[640px]:px-0 max-[640px]:w-2 shrink-0 select-none">
              =
            </span>

            {/* Result box */}
            <div className="w-[116px] flex-[0_0_116px] min-w-0 max-[900px]:w-[92px] max-[900px]:flex-[0_0_92px] max-[640px]:w-[60px] max-[640px]:flex-[0_0_60px]">
              <div className="h-[120px] max-[900px]:h-[96px] max-[640px]:h-[70px] relative rounded-lg bg-surface-container border border-outline/70 overflow-hidden shadow-sm flex items-center justify-center">
                {normalizedStatus === "failed" ? (
                  <div
                    className="flex flex-col justify-center items-center gap-1.5 max-[640px]:gap-0.5 w-full h-full p-2 max-[640px]:p-1 bg-red-50/50"
                  >
                    <MdError className="text-red-600 text-[24px] max-[640px]:text-[16px]" />
                    <span className="text-[10px] max-[640px]:text-[8px] font-bold text-red-700 uppercase tracking-wider text-center">Failed</span>
                  </div>
                ) : shouldShowLoader || normalizedStatus === "processing" ? (
                  <div className="flex flex-col justify-center items-center gap-2 w-full h-full p-2.5 bg-slate-50/70">
                    {/* Circular Progress Bar */}
                    <div className="relative w-11 h-11 flex items-center justify-center">
                      <svg className="w-full h-full" viewBox="0 0 36 36" style={{ transformOrigin: "center", animation: "spin 6s linear infinite" }}>
                        {/* Background Ring */}
                        <path
                          className="text-slate-100"
                          strokeWidth="3"
                          stroke="currentColor"
                          fill="none"
                          d="M18 2.0845
                            a 15.9155 15.9155 0 0 1 0 31.831
                            a 15.9155 15.9155 0 0 1 0 -31.831"
                        />
                        {/* Animated Progress Ring */}
                        <path
                          className="text-primary transition-all duration-300 ease-out"
                          strokeDasharray={`${Math.max(1, Math.min(100, displayProgress || 0))}, 100`}
                          strokeWidth="3.5"
                          strokeLinecap="round"
                          stroke="currentColor"
                          fill="none"
                          d="M18 2.0845
                            a 15.9155 15.9155 0 0 1 0 31.831
                            a 15.9155 15.9155 0 0 1 0 -31.831"
                        />
                      </svg>
                      <span className="absolute text-[10px] font-bold text-primary">
                        {Math.max(1, Math.min(100, displayProgress || 0))}%
                      </span>
                    </div>

                    <span className="text-[9px] max-[640px]:text-[7px] font-bold text-secondary uppercase tracking-wider animate-pulse">
                      Generating
                    </span>
                  </div>
                ) : normalizedStatus === "queued" ? (
                  <div className="flex flex-col justify-center items-center gap-1.5 w-full h-full p-2.5 bg-slate-50/70">
                    <MdOutlineQueue className="text-slate-400 text-[22px]" />
                    <span className="text-[9px] max-[640px]:text-[7px] font-bold text-secondary uppercase tracking-wider">
                      In Queue
                    </span>
                  </div>
                ) : (
                  <div className={`w-full h-full transition-opacity duration-300 ${imageFadeIn ? "opacity-100" : "opacity-90"}`}>
                    <OptimizedImage
                      src={result?.image}
                      className="w-full h-full object-contain"
                      alt="result"
                    />
                    {result?.image && (
                      <ZoomButton
                        modalTarget="#queueZoomModal"
                        onClick={() =>
                          openImageZoom(
                            getZoomImageSource(result.image),
                            "",
                            false,
                            false,
                          )
                        }
                        ariaLabel="Zoom result"
                        positionClasses="absolute bottom-1.5 right-1.5"
                        size="md"
                      />
                    )}
                  </div>
                )}
              </div>
              <p className="mt-1.5 text-xs max-[640px]:text-[9px] font-semibold text-primary text-center truncate">
                {normalizedStatus === "failed"
                  ? "Failed"
                  : normalizedStatus === "processing" || shouldShowLoader
                  ? "Generating"
                  : normalizedStatus === "queued"
                  ? "In Queue"
                  : "Generated"}
              </p>
            </div>
          </div>

          {normalizedStatus === "failed" && (
            <div
              className="mt-3 flex items-center gap-2 rounded-md border border-red-100 bg-red-50 px-3 py-2 text-left xl:mr-2 queue-error-banner"
              data-debug-error={queueItem.error_message || undefined}
            >
              <MdError className="text-[16px] text-red-700 shrink-0" />
              <p className="m-0 min-w-0 text-[11px] leading-4 text-red-800 break-words">
                {/* <span className="queue-error-label">Failure reason: </span>{" "} */}
                <span className="font-semibold">{displayErrorMessage}</span>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Right controls panel: absolute on desktop, static bottom row footer on mobile */}
      <div className="relative xl:absolute xl:right-4 xl:top-3 xl:bottom-3 xl:w-[136px] xl:flex-col xl:justify-between xl:items-end flex flex-row flex-wrap items-center justify-between w-full mt-3 pt-3 border-t xl:border-t-0 xl:mt-0 xl:pt-0 border-outline/40 z-20 select-none gap-3 sm:gap-0 min-w-0">
        {/* Info tooltip */}
        <div className="z-30">
          {renderParameterTooltip()}
        </div>

        {/* Action buttons list */}
        <div className="flex flex-row xl:flex-col gap-2 items-center xl:items-end xl:my-auto xl:py-1">
          {normalizedStatus === "completed" && (
            <>
              {/* Download Menu Button dropdown wrapper */}
              {canDownloadResult && (
                <div className="relative" ref={downloadMenuRef}>
                  <button
                    type="button"
                    onClick={toggleDownloadMenu}
                    className="px-3 xl:w-[124px] h-[36px] bg-[#1a1a1a] text-white hover:bg-slate-800 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer border-none queue-btn-primary"
                    aria-haspopup="menu"
                    aria-expanded={isDownloadMenuOpen}
                  >
                    <MdDownload className="text-[17px]" />Download
                  </button>

                  {isDownloadMenuOpen && (
                    <div
                      className="absolute bottom-full mb-1.5 right-0 min-w-[124px] p-1.5 rounded-lg bg-surface-container-lowest border border-outline shadow-xl z-[95]"
                      role="menu"
                      aria-label="Download formats"
                    >
                      {DOWNLOAD_FORMATS.map((format) => (
                        <button
                          key={format.value}
                          type="button"
                          className="w-full border-none rounded-md px-2.5 py-1.5 bg-transparent text-secondary hover:text-primary hover:bg-slate-50 text-[11px] font-bold text-left cursor-pointer transition-colors"
                          onClick={() => handleDownload(format)}
                          role="menuitem"
                        >
                          {format.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {canRegenerate && (
                <button
                  type="button"
                  onClick={handleRegenerate}
                  className="px-3 xl:w-[124px] h-[36px] border border-[#1a1a1a] text-[#1a1a1a] hover:bg-slate-50 bg-white rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer queue-btn-secondary"
                >
                  <MdRefresh className="text-[17px]" />Regenerate
                </button>
              )}
            </>
          )}

          {normalizedStatus === "failed" && canRegenerate && (
            <button
              type="button"
              onClick={handleRegenerate}
              className="px-3 xl:w-[124px] h-[36px] bg-[#1a1a1a] text-white hover:bg-slate-800 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer border-none queue-btn-primary"
            >
              <MdReplay className="text-[17px]" />Retry
            </button>
          )}
        </div>

        {/* Date / execution time info */}
        <div className="text-[11px] font-semibold text-secondary w-full min-w-0 max-w-full flex flex-row xl:flex-col items-center xl:items-end justify-between xl:justify-end gap-2.5 xl:gap-0.5 flex-wrap xl:flex-nowrap text-right overflow-hidden">
          <p className="m-0 leading-tight max-w-full truncate whitespace-nowrap" title={formatDisplayDate(date)}>{formatDisplayDate(date)}</p>
          {normalizedStatus === "completed" && executionTime != null && Number(executionTime) > 0 && (
            <p className="text-[10px] text-primary m-0 leading-tight max-w-full truncate whitespace-nowrap" title={`Time: ${executionTime}s`}>Time: {executionTime}s</p>
          )}
        </div>
      </div>

    </article>
  );
};

export default QueueItem;
